import { useSnackbar } from 'notistack';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { checkResData } from '../../../../utils/common';
import {
  getDailyReportDetail,
  getDailyReportList
} from '../../../../apicalls/dailyReport';
import {
  TDailyReportDetailApiResponse,
  TDailyReportDetailData,
  TDailyReportListData,
  TDailyReportPageForm
} from '../../../../types/dailyReport';
import {
  DAILY_REPORT_DVISION_SUPERVISER,
  DAILY_REPORT_DVISION_OUTSOURCE
} from '../../../../const/DailyReportConst';
import { convertFormDateToApiDate, log } from '../../../../utils';
import { parseNumber } from '../../../../utils/processAndTaskFormUtils';

export type Props = {
  isOpen: boolean;
  dailyReportId: number;
  setDailyReportId: Dispatch<number>;
  searchParams: TDailyReportPageForm;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  renderDailyReportTrigger: boolean;
};

export const useHooks = (props: Props) => {
  const {
    isOpen,
    dailyReportId,
    setDailyReportId,
    searchParams,
    currentPage,
    setCurrentPage,
    renderDailyReportTrigger
  } = props;

  const [disabledMoveNext, setDisabledMoveNext] = useState(false);
  const [disabledMovePrev, setDisabledMovePrev] = useState(false);

  // 「前へ」「次へ」で移動できる日報のIDの配列（1ページ分）
  const [dailyReportIdList, setDailyReportIdList] = useState<number[]>([]);

  // 現在のdisplay_limitでの総ページ数
  const [totalPageNum, setTotalPageNum] = useState<number>(0);

  // 「前へ」「次へ」のどちらの処理かの判定用
  const moveDirection = { prev: 0, init: 1, next: 2 };
  const moveKind = useRef(moveDirection.init);

  // 「次へ」の処理
  const moveNext = () => {
    const nextIndex = dailyReportIdList.indexOf(dailyReportId) + 1;
    // 現在保持している配列の範囲を超えたら次の範囲を取得
    if (nextIndex >= dailyReportIdList.length) {
      if (currentPage < totalPageNum) {
        moveKind.current = moveDirection.next;
        setCurrentPage(prev => prev + 1);
      }
    } else {
      setDailyReportId(dailyReportIdList[nextIndex]);
    }
  };

  // 「前へ」の処理
  const movePrev = () => {
    const prevIndex = dailyReportIdList.indexOf(dailyReportId) - 1;
    // 現在保持している配列の範囲を超えたら次の範囲を取得
    if (prevIndex < 0) {
      if (currentPage > 1) {
        moveKind.current = moveDirection.prev;
        setCurrentPage(prev => prev - 1);
      }
    } else {
      setDailyReportId(dailyReportIdList[prevIndex]);
    }
  };

  const [dailyReportDetail, setDailyReportDetail] =
    useState<TDailyReportDetailData | null>();

  const isSupervisor = () => {
    return (
      dailyReportDetail?.report_division === DAILY_REPORT_DVISION_SUPERVISER
    );
  };
  const isWorker = () => {
    return (
      dailyReportDetail?.report_division === DAILY_REPORT_DVISION_OUTSOURCE
    );
  };

  const [isLoading, setIsLoading] = useState(false);

  // snackbar
  const { enqueueSnackbar } = useSnackbar();

  // 検索条件に該当する日報IDの一覧を取得
  // 日報詳細の次へ/前へでページが切り替わば場合は、日報ID一覧を取得してから日報詳細を取得する。
  useEffect(() => {
    setIsLoading(true);
    const constructId = searchParams.construct_id
      ? searchParams.construct_id.value
      : 0;
    const reportDivision = parseNumber(searchParams.report_division);
    const displayLimit = parseNumber(searchParams.display_limit) || undefined;
    getDailyReportList(
      constructId,
      searchParams.worker_name,
      reportDivision,
      convertFormDateToApiDate(searchParams.start_date),
      convertFormDateToApiDate(searchParams.end_date),
      searchParams.cost_up,
      searchParams.cost_down,
      searchParams.images,
      currentPage,
      displayLimit
    ).then(resData => {
      checkResData(resData, enqueueSnackbar).then((data: any) => {
        if (data) {
          const idList = data.data.map((report: TDailyReportListData) => {
            return report.id;
          });
          setDailyReportIdList(idList);

          if (moveKind.current === moveDirection.next) {
            // 「次へ」で範囲を取り直した場合は先頭の要素
            setDailyReportId(idList[0]);
          } else if (moveKind.current === moveDirection.prev) {
            // 「前へ」で範囲を取り直した場合は末尾の要素
            setDailyReportId(idList[(displayLimit ?? 1) - 1]);
          }

          setTotalPageNum(data.page_count);
        }
        setIsLoading(false);
      });
    });
  }, [searchParams, currentPage, renderDailyReportTrigger]);

  // 詳細表示処理
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      getDailyReportDetail(dailyReportId).then((resData: any) => {
        checkResData(resData, enqueueSnackbar).then((data: any) => {
          if (data) {
            const response: TDailyReportDetailApiResponse = data;

            setDailyReportDetail(response.data);
          }
          setIsLoading(false);
        });
      });

      const currentIndex = dailyReportIdList.indexOf(dailyReportId);
      // 「前へ」のdisabled設定
      setDisabledMovePrev(currentIndex <= 0 && currentPage <= 1);
      // 「次へ」のdisabled設定
      setDisabledMoveNext(
        currentIndex >= dailyReportIdList.length - 1 &&
          currentPage >= totalPageNum
      );

      // 日報詳細閲覧時にログ出力
      log.info(`to day-report detail: report id: ${dailyReportId}`);
    }
  }, [isOpen, dailyReportId]);

  return {
    moveNext,
    movePrev,
    dailyReportDetail,
    disabledMovePrev,
    disabledMoveNext,
    isSupervisor,
    isWorker,
    isLoading
  };
};
